import { API } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

async function call(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    /* empty */
  }
  if (!res.ok) {
    const msg = formatApiErrorDetail(payload?.detail) || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return payload;
}

export const adminApi = {
  engines: () => call("/engines"),
  setEngine: (engine) => call("/admin/engine", { method: "POST", body: { engine } }),
  runtimeEngines: () => call("/admin/runtime-engines"),
  saveRuntimeEngine: (payload) => call("/admin/runtime-engines", { method: "POST", body: payload }),
  deleteRuntimeEngine: (engine) => call(`/admin/runtime-engines/${encodeURIComponent(engine)}`, { method: "DELETE" }),
  eval: (query, system_prompt_override) =>
    call("/admin/eval", { method: "POST", body: { query, system_prompt_override } }),
  stats: () => call("/admin/stats"),
  audit: (limit = 50) => call(`/admin/audit?limit=${limit}`),
  users: (limit = 100) => call(`/admin/users?limit=${limit}`),
  createUser: ({ email, password, name, role }) =>
    call("/admin/users", { method: "POST", body: { email, password, name, role } }),
  resetUserPassword: (userId, new_password) =>
    call(`/admin/users/${userId}/password`, { method: "POST", body: { new_password } }),
  setUserRole: (userId, role) =>
    call(`/admin/users/${userId}/role`, { method: "POST", body: { role } }),
  setUserVoiceBeta: (userId, voice_beta) =>
    call(`/admin/users/${userId}/voice-beta`, { method: "POST", body: { voice_beta } }),
  setUserDirectVoiceBeta: (userId, direct_voice_beta) =>
    call(`/admin/users/${userId}/direct-voice-beta`, { method: "POST", body: { direct_voice_beta } }),
  changeOwnPassword: (current_password, new_password) =>
    call("/auth/change-password", {
      method: "POST",
      body: { current_password, new_password },
    }),
  memoryEval: (query) =>
    call("/admin/memory-eval", { method: "POST", body: { query } }),
  memoryEvalVote: (query, winner, notes) =>
    call("/admin/memory-eval/vote", {
      method: "POST",
      body: { query, winner, notes },
    }),
  engineHealth: (minutes = 60) =>
    call(`/admin/engine-health?minutes=${minutes}`),
  failoverLog: (limit = 20) =>
    call(`/admin/failover-log?limit=${limit}`),
  userAnalytics: () => call(`/admin/user-analytics`),
  hbmEval: (query) =>
    call("/admin/hbm-eval", { method: "POST", body: { query } }),
  hbmEvalVote: (run_id, branch, rating, notes) =>
    call("/admin/hbm-eval/vote", {
      method: "POST",
      body: { run_id, branch, rating, notes },
    }),
  hbmEvalSummary: () => call("/admin/hbm-eval/summary"),
  realtimeUsage: () => call("/admin/realtime/usage"),
  // Dashboard endpoints
  dashboardOverview: () => call("/admin/dashboard/overview"),
  dashboardMetrics: () => call("/admin/dashboard/metrics"),
  dashboardHealth: () => call("/admin/dashboard/health"),
  dashboardSubscriptions: () => call("/admin/dashboard/subscriptions"),
  dashboardApiKeys: () => call("/admin/dashboard/api-keys"),
  dashboardUsers: ({ page = 1, page_size = 25, q = "", include_deleted = false } = {}) => {
    const params = new URLSearchParams({ page, page_size });
    if (q) params.set("q", q);
    if (include_deleted) params.set("include_deleted", "true");
    return call(`/admin/dashboard/users?${params.toString()}`);
  },
  setUserLimits: (userId, limits) =>
    call(`/admin/dashboard/users/${userId}/limits`, { method: "PATCH", body: limits }),
  forceLogoutUser: (userId) =>
    call(`/admin/dashboard/users/${userId}/force-logout`, { method: "POST" }),
  toggleUserMemory: (userId) =>
    call(`/admin/dashboard/users/${userId}/toggle-memory`, { method: "POST" }),
  softDeleteUser: (userId) =>
    call(`/admin/dashboard/users/${userId}`, { method: "DELETE" }),
  restoreUser: (userId) =>
    call(`/admin/dashboard/users/${userId}/restore`, { method: "POST" }),
  // Curation — turn great real chats into corpus gold.
  listCurationExchanges: ({ limit = 50, pool = "invited", sinceDays = 30 } = {}) =>
    call(`/admin/curation/exchanges?limit=${limit}&pool=${encodeURIComponent(pool)}&since_days=${sinceDays}`),
  promoteExchange: ({ conv_id, index, labels, note } = {}) =>
    call("/admin/curation/promote", {
      method: "POST",
      body: { conv_id, index, labels, note },
    }),
  unpromoteExchange: ({ conv_id, index } = {}) =>
    call("/admin/curation/unpromote", {
      method: "POST",
      body: { conv_id, index },
    }),
  // Research invites — invitees auto-consent to training/research use.
  listResearchInvites: () => call("/admin/research-invites"),
  createResearchInvite: ({ email, label } = {}) =>
    call("/admin/research-invites", { method: "POST", body: { email, label } }),
  revokeResearchInvite: (token) =>
    call(`/admin/research-invites/${encodeURIComponent(token)}`, { method: "DELETE" }),
  // Direct download endpoints (return raw JSONL). We don't parse — the
  // browser gets a file download via anchor.href.
  researchExportAllUrl: () => `${API}/admin/research-invites/export-all`,
  researchExportUserUrl: (userId) =>
    `${API}/admin/research-invites/export/${encodeURIComponent(userId)}`,
  // Super-admin — runtime allowlist for full-access accounts.
  listFullAccessAllowlist: () => call("/admin/superadmin/full-access"),
  addFullAccessAllowlist: (email) =>
    call("/admin/superadmin/full-access", { method: "POST", body: { email } }),
  removeFullAccessAllowlist: (email) =>
    call(`/admin/superadmin/full-access/${encodeURIComponent(email)}`, { method: "DELETE" }),
  // Analytics
  analyticsOverview: () => call("/admin/analytics/overview"),
  analyticsTimeseries: (days = 30) => call(`/admin/analytics/timeseries?days=${days}`),
  analyticsGeo: (days = 30) => call(`/admin/analytics/geo?days=${days}`),
  analyticsDevices: (days = 30) => call(`/admin/analytics/devices?days=${days}`),
  analyticsTraffic: (days = 30) => call(`/admin/analytics/traffic?days=${days}`),
  analyticsUser: (userId) => call(`/admin/analytics/user/${encodeURIComponent(userId)}`),
  sendRetentionEmail: (userId, kind) =>
    call(`/admin/analytics/user/${encodeURIComponent(userId)}/email`, {
      method: "POST",
      body: { kind },
    }),
  analyticsLiveSessions: (minutes = 15) => call(`/admin/analytics/live-sessions?minutes=${minutes}`),
  analyticsAnomalies: () => call("/admin/analytics/anomalies"),
  analyticsSessions: (days = 7, limit = 100) =>
    call(`/admin/analytics/sessions?days=${days}&limit=${limit}`),
  analyticsSession: (sessionId) =>
    call(`/admin/analytics/session/${encodeURIComponent(sessionId)}`),
  trainingCorpus: ({ limit = 25, skip = 0, q = "", basis = "all", role = "all", days = 90 } = {}) => {
    const params = new URLSearchParams({ limit, skip, q, basis, role, days });
    return call(`/admin/training-corpus?${params.toString()}`);
  },
  trainingCorpusExportUrl: ({ format = "jsonl", q = "", basis = "all", role = "all", days = 90 } = {}) => {
    const params = new URLSearchParams({ format, q, basis, role, days });
    return `${API}/admin/training-corpus/export?${params.toString()}`;
  },
};

// Public preflight — used by the register page to detect an invite link.
export async function preflightResearchInvite(token) {
  const res = await fetch(`${API}/research-invites/${encodeURIComponent(token)}`, {
    credentials: "include",
  });
  if (!res.ok) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}
